import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileCheck, Search, Download } from 'lucide-react';
import api from '../lib/api';
import { setPageMeta, fmtDate, catOf } from '../lib/shop';

export default function Coas() {
  const [coas, setCoas] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => { setPageMeta('COA Library', 'Search batch Certificates of Analysis by product or batch number.'); }, []);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      api.get('/coas', { params: q ? { q } : {} }).then((r) => setCoas(r.data)).catch(() => {}).finally(() => setLoading(false));
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="coa-page">
      <p className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-[#8A8F95]">Batch Documentation</p>
      <h1 className="font-display font-black uppercase text-4xl sm:text-5xl tracking-tight mt-2">COA Library</h1>
      <p className="text-[#575B60] mt-3 max-w-2xl">Every certificate below is a genuine, batch-specific document. Search by product name or batch number to verify the documentation attached to your material.</p>

      <div className="flex items-center gap-3 border-b-2 border-[#0B0F12] mt-8 pb-3 max-w-xl">
        <Search className="w-5 h-5 text-[#8A8F95]" />
        <input data-testid="coa-search-input" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search product or batch number…" className="flex-1 bg-transparent focus:outline-none text-lg placeholder:text-[#B7BCC1]" />
      </div>

      <div className="mt-8 space-y-3">
        {loading && <p className="font-mono2 text-xs uppercase tracking-widest text-[#8A8F95] py-8">Loading…</p>}
        {!loading && coas.length === 0 && (
          <div className="border hairline bg-white p-10 text-center" data-testid="coa-empty">
            <FileCheck className="w-8 h-8 mx-auto text-[#D8D8D0]" />
            <p className="font-display font-bold uppercase text-xl mt-4">{q ? 'No certificates match your search' : 'No certificates published yet'}</p>
            <p className="text-sm text-[#575B60] mt-2 max-w-md mx-auto">
              Certificates appear here as batch documentation is uploaded. We never publish placeholder or representative results.
            </p>
          </div>
        )}
        {coas.map((c) => (
          <div key={c.id} data-testid={`coa-row-${c.id}`} className="border hairline bg-white p-5 flex flex-col sm:flex-row sm:items-center gap-4 hover:border-[#0B0F12] transition-colors">
            <div className="w-10 h-10 bg-[#0B0F12] flex items-center justify-center shrink-0">
              <FileCheck className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-display font-bold uppercase text-lg leading-tight">{c.product_name}</p>
              <p className="font-mono2 text-[10px] uppercase tracking-wider text-[#8A8F95] mt-1">
                Batch {c.batch} · {c.laboratory} · {c.test_type}
              </p>
              {c.result_specification && <p className="text-sm text-[#575B60] mt-1">{c.result_specification}</p>}
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="font-mono2 text-[10px] uppercase tracking-wider text-[#8A8F95]">Test date</p>
                <p className="font-mono2 text-xs">{fmtDate(c.test_date)}</p>
              </div>
              {c.pdf_url && (
                <a data-testid={`coa-pdf-${c.id}`} href={c.pdf_url} target="_blank" rel="noreferrer"
                  className="bg-[#0B0F12] text-white px-4 py-2.5 font-mono2 text-[10px] uppercase tracking-widest flex items-center gap-2 hover:bg-[#E54D2E] transition-colors">
                  <Download className="w-3.5 h-3.5" /> PDF
                </a>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-12 border hairline bg-white p-6">
        <p className="font-display font-bold uppercase text-xl">How to verify your batch</p>
        <p className="text-sm text-[#575B60] mt-2 leading-relaxed">
          Find the batch number printed on your unit, search it above, and confirm the certificate matches the product and batch.
          Questions about a certificate? Contact <a className="underline" href="mailto:support@vqhealth.co.uk">support@vqhealth.co.uk</a>.
        </p>
        <Link to="/docs/coas" className="inline-block mt-4 font-mono2 text-[10px] uppercase tracking-widest underline underline-offset-4">Read: what a COA records</Link>
      </div>
    </div>
  );
}
